import CheckIcon from "@mui/icons-material/Check";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataGridSettings } from "../DataGridSettingsProvider";
import ColorInterval from "@/models/ColorInterval";
import { DiagnosticSettingsSlotProps } from "@/models/CustomColumnMenuProps";

type EditableColorInterval = {
  min: number | string | null;
  max: number | string | null;
  color: string;
  backgroundColor: string;
};

export default function IntervalMenu(props: DiagnosticSettingsSlotProps) {
  const { colorIntervals, setColorIntervals } = useDataGridSettings();
  const field = props.field;

  const [localIntervals, setLocalIntervals] = useState<EditableColorInterval[]>(
    colorIntervals[field] || [],
  );
  const [isEditing, setIsEditing] = useState(false);

  const handleAddInterval = () => {
    setLocalIntervals(prev => [
      ...prev,
      { min: "", max: "", color: "#000000", backgroundColor: "#ffffff" },
    ]);
    setIsEditing(true);
  };

  const handleUpdateInterval = (index: number, key: keyof EditableColorInterval, value: string) => {
    setLocalIntervals(prev =>
      prev.map((interval, i) => (i === index ? { ...interval, [key]: value } : interval)),
    );
    setIsEditing(true);
  };

  const removeInterval = (index: number) => {
    setLocalIntervals(prev => prev.filter((_, i) => i !== index));
    setIsEditing(true);
  };

  const validateAndSaveIntervals = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    let sortedIntervals = [...localIntervals];

    if (
      sortedIntervals.some(
        interval =>
          interval.min === undefined ||
          interval.max === undefined ||
          interval.min === null ||
          interval.max === null ||
          interval.min === "" ||
          interval.max === "" ||
          isNaN(Number(interval.min)) ||
          isNaN(Number(interval.max)),
      )
    ) {
      alert("Min and Max values must be valid numbers and cannot be empty!");
      return;
    }

    const intervals: ColorInterval[] = sortedIntervals.map(interval => ({
      ...interval,
      min: Number(interval.min),
      max: Number(interval.max),
    }));

    if (intervals.some(interval => interval.min >= interval.max)) {
      alert("Each Min must be less than Max!");
      return;
    }

    intervals.sort((a, b) => (a.min ?? 0) - (b.min ?? 0));

    for (let i = 0; i < intervals.length - 1; i++) {
      if (intervals[i].max > intervals[i + 1].min) {
        alert("Intervals must not overlap!");
        return;
      }
    }
    setColorIntervals(prev => ({ ...prev, [field]: intervals }));
    setIsEditing(false);
  };

  const { t } = useTranslation();
  return (
    <div>
      <MenuItem onClick={handleAddInterval}>
        <ListItemIcon>
          <ColorLensIcon fontSize="small" />
        </ListItemIcon>
        <Typography>{t("faultOverview.colorIntervalsTitle")}</Typography>

        {isEditing && (
          <IconButton onClick={validateAndSaveIntervals} color="success">
            <CheckIcon />
          </IconButton>
        )}
      </MenuItem>
      {localIntervals.map((interval, index) => (
        <MenuItem key={index} sx={{ gap: 1 }}>
          <IconButton onClick={() => removeInterval(index)}>
            <DeleteIcon />
          </IconButton>
          <TextField
            size="small"
            sx={{ width: "90px" }}
            type="number"
            value={interval.min ?? ""}
            onKeyDown={e => {
              e.stopPropagation();
            }}
            onChange={e => handleUpdateInterval(index, "min", e.target.value)}
            placeholder="Min"
          />
          <TextField
            size="small"
            sx={{ width: "90px" }}
            type="number"
            value={interval.max ?? ""}
            onKeyDown={e => {
              e.stopPropagation();
            }}
            onChange={e => handleUpdateInterval(index, "max", e.target.value)}
            placeholder="Max"
          />
          <TextField
            size="small"
            sx={{ width: "50px" }}
            type="color"
            value={interval.color}
            onChange={e => handleUpdateInterval(index, "color", e.target.value)}
            title={t("faultOverview.value")}
          />
          <TextField
            size="small"
            sx={{ width: "50px" }}
            type="color"
            value={interval.backgroundColor}
            onChange={e => handleUpdateInterval(index, "backgroundColor", e.target.value)}
            title={t("faultOverview.background")}
          />
        </MenuItem>
      ))}
    </div>
  );
}
